import { DefaultLayout } from '@c/layouts';
import Input from '@ui/Input';
import PaginatorClient from '@ui/Paginator';
import SafeImage from '@ui/SafeImage';
import { cx } from 'class-variance-authority';
import { GetStaticProps } from 'next';
import Link from 'next/link';
import { ReactElement, useState } from 'react';
import { BrandObj, getBrands } from '../api/brands';
import TopBrands from '@c/TopBrands';
import SEO from '@c/SEO';
import Breadcrumb from '@ui/Breadcrumb';

const alpha = Array.from(Array(26)).map((e, i) => i + 65);
const alphabet = alpha.map((x) => String.fromCharCode(x));

export type BrandProp = BrandObj & { url: string };
type BrandsPageProps = {
  brands: BrandProp[];
};

function getFilteredBrands({
  brands,
  search,
  startsWith,
}: {
  brands: BrandProp[];
  search: string;
  category?: string;
  startsWith: string;
}) {
  if (startsWith && startsWith !== 'ALL') {
    if (startsWith === '#') {
      brands = brands.filter((b) => !!b.name.match(/^\d/g));
    } else {
      brands = brands.filter((b) =>
        b.name.toLowerCase().startsWith(startsWith.toLowerCase())
      );
    }
  }
  if (search) {
    brands = brands.filter((b) =>
      b.name.toLowerCase().includes(search.toLowerCase())
    );
  }
  return brands;
}

const Brands = ({ brands }: BrandsPageProps) => {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    sort: '',
    charFilter: '',
  });
  const [page, setPage] = useState(1);
  const currentBrands = getFilteredBrands({
    brands,
    search,
    startsWith: filters.charFilter,
  });
  const pageSize = 15;
  const totalPages = Math.floor(currentBrands.length / pageSize);
  const start = (page - 1) * pageSize;
  const end = Math.min(start + pageSize, currentBrands.length);

  const handleSelectChange = (value: string, name: string) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
  const charFilters = ['ALL', ...alphabet, '#'];
  return (
    <>
      <SEO
        title="Camera & Photography Brands"
        description="Explore top camera and photography brands. Find equipment from Canon, Nikon, Sony, Fujifilm and more, all in one marketplace."
        ogImage="/images/brands-banner.jpg"
        keywords={[
          'camera brands', 'photography brands', 'Canon', 'Nikon', 'Sony', 
          'Fujifilm', 'Leica', 'professional camera brands', 'lens brands'
        ]}
      />
      
      <div className="m-auto flex w-full max-w-default flex-col gap-[2.4rem] px-4 sm:gap-[4rem] sm:px-default-padding-x">
        {/* Breadcrumbs */}
        <div className="w-full mt-4">
          <Breadcrumb 
            items={[
              { label: 'Home', href: '/' },
              { label: 'Brands', href: '/brands' }
            ]}
          />
        </div>
        
        <h1 className="mt-[2rem] mb-[-3rem] text-[3rem] font-semibold">Top Brands</h1>
        <TopBrands />
        
        {/* search row */}
        <div className="flex flex-col gap-[1.6rem] sm:flex-row pt-[5rem]">

          <div className="w-full grow ">
            <Input
              type="search"
              placeholder="Find your favorite brands..."
              onChange={(e) => setSearch(e.target.value)}
              full
            />
          </div>
          {/* <div className="flex flex-col sm:flex-row items-center gap-[1.6rem] grow justify-end">
            <p className="hidden sm:block font-semibold">Filter</p>
            <div className="grow w-full sm:max-w-[20rem]">
              <Select
                value={filters.category}
                options={categoryOptions}
                onChange={(option) =>
                  handleSelectChange(option.value, 'category')
                }
              />
            </div>
            <div className="grow w-full sm:max-w-[16.9rem]">
              <Select
                value={filters.sort}
                options={sortOptions}
                onChange={(option) => handleSelectChange(option.value, 'sort')}
              />
            </div>
          </div> */}
        </div>
        {/* char filter row */}
        <div className="flex w-full justify-between overflow-x-scroll">
          {charFilters.map((c) => {
            const selected = c === filters.charFilter;
            return (
              <button
                className={`mx-2 font-semibold transition-colors hover:text-brand-primary ${
                  selected ? 'text-brand-primary' : 'text-brand-dark-gray'
                }`}
                key={c}
                onClick={() => handleSelectChange(c, 'charFilter')}
              >
                {c.toUpperCase()}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-[2.4rem]">
          {currentBrands.slice(start, end).map((b) => {
            return (
              <Link
                href={'/brands/' + b.url}
                key={b.id}
                className={cx([
                  'relative flex h-[18rem] min-w-[15rem] grow items-center justify-center rounded-[1rem] border-2 border-brand-lightest-gray sm:min-w-[22rem] sm:max-w-[22rem]',
                  'opacity-70 grayscale transition-all duration-150 hover:opacity-100 hover:grayscale-0',
                ])}
              >
                {b.image?.startsWith('http') ? (
                  <SafeImage
                    src={b.image}
                    alt={b.name}
                    className="object-contain"
                    fill
                  />
                ) : (
                  <h1 className="text-[2.4rem] font-semibold">{b.name}</h1>
                )}
              </Link>
            );
          })}
        </div>

        <PaginatorClient
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(n) => setPage(n)}
        />
      </div>
    </>
  );
};

export const getStaticProps: GetStaticProps<BrandsPageProps> = async () => {
  const brands = await getBrands();

  return {
    props: {
      brands
    },
  };
};

Brands.getLayout = function getLayout(page: ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export default Brands;
